import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import config from '../config';

const BackgroundTaskContext = createContext();

export const BackgroundTaskProvider = ({ children }) => {
  // Initialize from localStorage
  const [tasks, setTasks] = useState(() => {
    try {
      const savedTasks = localStorage.getItem('backgroundTasks');
      if (!savedTasks) return [];

      const allTasks = JSON.parse(savedTasks);

      // Only restore 'processing' tasks from previous sessions
      // Discard 'completed' and 'failed' tasks to avoid polling old jobs
      const processingTasks = allTasks.filter(task => task.status === 'processing');

      if (processingTasks.length < allTasks.length) {
        console.log(
          `[BackgroundTaskContext] Loaded ${processingTasks.length} processing tasks, ` +
          `discarded ${allTasks.length - processingTasks.length} completed/failed tasks from previous session`
        );
      }

      return processingTasks;
    } catch (error) {
      console.error('Error loading tasks from localStorage:', error);
      return [];
    }
  });

  // Persist tasks to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('backgroundTasks', JSON.stringify(tasks));
    } catch (error) {
      console.error('Error saving tasks to localStorage:', error);
    }
  }, [tasks]);

  const addTask = useCallback((task) => {
    const taskId = `task_${Date.now()}_${Math.random()}`;
    const newTask = {
      id: taskId,
      status: 'processing', // 'processing', 'completed', 'failed'
      progress: 0,
      ...task,
      createdAt: Date.now()
    };
    setTasks(prev => [...prev, newTask]);
    return taskId;
  }, []);

  const updateTask = useCallback((taskId, updates) => {
    console.log(`[BackgroundTaskContext] Updating task ${taskId}:`, updates);
    setTasks(prev => {
      const originalTask = prev.find(t => t.id === taskId);
      const updated = prev.map(task =>
        task.id === taskId ? { ...task, ...updates } : task
      );
      const updatedTask = updated.find(t => t.id === taskId);

      if (originalTask && updatedTask) {
        if (originalTask.status !== updatedTask.status) {
          console.log(`[BackgroundTaskContext] ✅ Task status changed: ${originalTask.status} → ${updatedTask.status}`);
        }
      }

      return updated;
    });
  }, []);

  const removeTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  const clearCompletedTasks = useCallback(() => {
    setTasks(prev =>
      prev.filter(task => task.status === 'processing')
    );
  }, []);

  const getCompletedTasks = useCallback(() => {
    return tasks.filter(task => task.status === 'completed' || task.status === 'failed');
  }, [tasks]);

  const getCompletedCount = useCallback(() => {
    return tasks.filter(task => task.status === 'completed').length;
  }, [tasks]);

  const cancelTask = useCallback((taskId) => {
    console.log('[CancelTask] Attempting to cancel task:', taskId);
    console.log('[CancelTask] All tasks:', tasks);

    // Find the task directly without closure issues
    const currentTasks = tasks; // Use current value at time of call
    const task = currentTasks.find(t => t.id === taskId);
    console.log('[CancelTask] Found task:', task);

    if (!task) {
      console.error('[CancelTask] Task not found');
      return;
    }

    if (!task.jobId) {
      console.error('[CancelTask] Task has no jobId:', task);
      return;
    }

    const API_URL = config.API_BASE_URL;
    console.log(`[CancelTask] Sending cancel request to ${API_URL}/job/cancel/${task.jobId}`);

    // First, immediately update the task to show it's being cancelled
    console.log('[CancelTask] Immediately marking task as failed (before API call)');
    updateTask(taskId, {
      status: 'failed',
      resultMessage: 'Job cancelled by user'
    });

    // Use async IIFE to avoid useCallback async issues
    (async () => {
      try {
        const response = await fetch(`${API_URL}/job/cancel/${task.jobId}`, {
          method: 'POST'
        });

        console.log('[CancelTask] Response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('[CancelTask] Cancel request to backend successful:', data);
        } else {
          const errorData = await response.json();
          console.error('[CancelTask] Backend cancel failed:', errorData);
          // Task is already marked as failed, so we keep it that way
        }
      } catch (error) {
        console.error('[CancelTask] Error sending cancel request to backend:', error);
        // Task is already marked as failed, so we keep it that way
      }
    })();
  }, [tasks, updateTask]);

  return (
    <BackgroundTaskContext.Provider
      value={{
        tasks,
        addTask,
        updateTask,
        removeTask,
        cancelTask,
        clearCompletedTasks,
        getCompletedTasks,
        getCompletedCount
      }}
    >
      {children}
    </BackgroundTaskContext.Provider>
  );
};

export const useBackgroundTask = () => {
  const context = useContext(BackgroundTaskContext);
  if (!context) {
    throw new Error('useBackgroundTask must be used within BackgroundTaskProvider');
  }
  return context;
};
